/*
 * Purpose: Mission-scoped automation gates — auto-connect/deploy require explicit human opt-in
 * Last modified: 2026-06-28 | Agent
 * Completeness: 100
 */

/** Actions that stay behind human gates unless the user opts in for this Mission only. */
export type MissionAutomationScope = 'auto_connect' | 'auto_deploy';

export type MissionAutomationScopeConsent = {
  enabled: boolean;
  approvedAt?: string;
  /** Optional user note — e.g. which connector or deploy target */
  note?: string;
};

export type MissionAutomationConsent = {
  scopes: Partial<Record<MissionAutomationScope, MissionAutomationScopeConsent>>;
};

export const DEFAULT_MISSION_AUTOMATION_CONSENT: MissionAutomationConsent = { scopes: {} };

export const MISSION_AUTOMATION_SCOPE_LABELS: Record<
  MissionAutomationScope,
  { title: string; description: string }
> = {
  auto_connect: {
    title: 'Auto-connect for this Mission',
    description:
      'Allow AutoBlocks to connect OAuth/API credentials without asking each time — only for steps in this Mission.',
  },
  auto_deploy: {
    title: 'Auto-deploy for this Mission',
    description:
      'Allow AutoBlocks to publish or deploy changes after dry-run passes — only for assets scoped to this Mission.',
  },
};

export function normalizeMissionAutomationConsent(
  raw?: MissionAutomationConsent | null
): MissionAutomationConsent {
  if (!raw?.scopes || typeof raw.scopes !== 'object') {
    return { ...DEFAULT_MISSION_AUTOMATION_CONSENT };
  }
  return {
    scopes: {
      auto_connect: raw.scopes.auto_connect?.enabled
        ? { ...raw.scopes.auto_connect, enabled: true }
        : undefined,
      auto_deploy: raw.scopes.auto_deploy?.enabled
        ? { ...raw.scopes.auto_deploy, enabled: true }
        : undefined,
    },
  };
}

export function isMissionAutomationAllowed(
  consent: MissionAutomationConsent | undefined,
  scope: MissionAutomationScope
): boolean {
  return consent?.scopes[scope]?.enabled === true;
}

export function canAutoConnectForMission(consent?: MissionAutomationConsent): boolean {
  return isMissionAutomationAllowed(consent, 'auto_connect');
}

export function canAutoDeployForMission(consent?: MissionAutomationConsent): boolean {
  return isMissionAutomationAllowed(consent, 'auto_deploy');
}

export function setMissionAutomationScope(
  consent: MissionAutomationConsent,
  scope: MissionAutomationScope,
  enabled: boolean,
  note?: string
): MissionAutomationConsent {
  const next = normalizeMissionAutomationConsent(consent);
  if (!enabled) {
    const { [scope]: _removed, ...rest } = next.scopes;
    return { scopes: rest };
  }
  return {
    scopes: {
      ...next.scopes,
      [scope]: {
        enabled: true,
        approvedAt: new Date().toISOString(),
        note: note?.trim() || undefined,
      },
    },
  };
}
